import { useState } from "react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Mail, Truck, ShieldQuestion, Briefcase, MessageSquareHeart } from "lucide-react";

const TOPICS = [
  { value: "order", label: "Order or shipping issue", icon: Truck, hint: "Lost / damaged / spoiled? Please include order number and clear photos." },
  { value: "account", label: "Account, billing, or refunds", icon: ShieldQuestion, hint: "We respond to refund requests within one business day." },
  { value: "partnership", label: "Farm partner inquiry", icon: Briefcase, hint: "Apply to bring your farm onto the platform." },
  { value: "press", label: "Press, investors, or partnerships", icon: MessageSquareHeart, hint: "Tell us a little about your organization." },
  { value: "other", label: "Something else", icon: Mail, hint: "We'll route your message to the right team." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "order", order_id: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/support/contact", form);
      setTicketId(data.ticket_id);
    } catch (err) {
      setError(err.response?.data?.detail || "We couldn't send your message — please email hello@thefarmtoyou.com directly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="contact-page">
      <Seo title="Contact · The Farm To You" description="Reach the member-experience team. We typically respond within one business day." canonical="/contact"/>

      <p className="overline">Talk to us</p>
      <h1 className="font-display text-5xl md:text-6xl mt-3">We're real people. We answer back.</h1>
      <p className="mt-4 max-w-2xl text-[var(--ff-text-secondary)] leading-relaxed">
        The member-experience team responds within one business day, Monday through Friday. For urgent shipment issues (damage or spoilage on delivery), please reach out within 48 hours and include clear photos so we can issue a refund or replacement quickly.
      </p>

      <div className="mt-12 grid md:grid-cols-3 gap-8">
        <aside className="md:col-span-1 space-y-4" data-testid="contact-direct">
          <div className="card-flat p-5">
            <p className="overline">Email</p>
            <a href="mailto:hello@thefarmtoyou.com" className="block mt-2 text-[var(--ff-primary)] font-medium">hello@thefarmtoyou.com</a>
            <p className="text-xs text-[var(--ff-text-muted)] mt-1">General member inquiries</p>
          </div>
          <div className="card-flat p-5">
            <p className="overline">Refunds &amp; orders</p>
            <a href="mailto:orders@thefarmtoyou.com" className="block mt-2 text-[var(--ff-primary)] font-medium">orders@thefarmtoyou.com</a>
            <p className="text-xs text-[var(--ff-text-muted)] mt-1">Reply to your order confirmation for fastest service.</p>
          </div>
          <div className="card-flat p-5">
            <p className="overline">Press &amp; partnerships</p>
            <a href="mailto:press@thefarmtoyou.com" className="block mt-2 text-[var(--ff-primary)] font-medium">press@thefarmtoyou.com</a>
          </div>
        </aside>

        <form onSubmit={onSubmit} className="md:col-span-2 card-flat p-6 space-y-5 relative" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="overline">Your name</span>
              <input required className="input-base mt-2" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="contact-name"/>
            </label>
            <label className="block">
              <span className="overline">Email</span>
              <input required type="email" className="input-base mt-2" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="contact-email"/>
            </label>
          </div>

          <div>
            <span className="overline">What can we help with?</span>
            <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="contact-topics">
              {TOPICS.map(t => {
                const Icon = t.icon;
                const active = form.topic === t.value;
                return (
                  <button key={t.value} type="button" onClick={() => setForm({...form, topic: t.value})} data-testid={`contact-topic-${t.value}`}
                    className={`text-left p-3 rounded-lg border transition-all flex gap-3 ${active ? "bg-[var(--ff-primary)]/10 border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] hover:border-[var(--ff-primary)]"}`}>
                    <Icon size={16} className="mt-1 shrink-0 text-[var(--ff-primary)]"/>
                    <div>
                      <p className="text-sm font-medium">{t.label}</p>
                      <p className="text-[11px] text-[var(--ff-text-muted)] mt-1 leading-snug">{t.hint}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {(form.topic === "order" || form.topic === "account") && (
            <label className="block">
              <span className="overline">Order ID (if applicable)</span>
              <input className="input-base mt-2" placeholder="e.g. seed-order-sunshine-1" value={form.order_id} onChange={e => setForm({...form, order_id: e.target.value})} data-testid="contact-order-id"/>
            </label>
          )}

          <label className="block">
            <span className="overline">Message</span>
            <textarea required minLength={10} rows={6} className="input-base mt-2" placeholder="Tell us what's going on. Photos can be attached in a reply email after you submit." value={form.message} onChange={e => setForm({...form, message: e.target.value})} data-testid="contact-message"/>
          </label>

          {/* Honeypot — hidden from real users */}
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
            className="absolute opacity-0 pointer-events-none -z-10 h-0 w-0"
            value={form.website} onChange={e => setForm({...form, website: e.target.value})}
            data-testid="contact-honeypot"/>

          {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="contact-error">{error}</div>}

          {ticketId ? (
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded text-sm text-emerald-800" data-testid="contact-sent">
              Got it — ticket <strong className="font-mono">{ticketId}</strong> opened. Our team will reply within one business day.
            </div>
          ) : (
            <button disabled={busy} type="submit" className="btn-primary" data-testid="contact-submit">
              {busy ? "Sending…" : "Send message"}
            </button>
          )}

          <p className="text-xs text-[var(--ff-text-muted)]">
            By submitting this form, you agree to our <a href="/terms" className="underline">Terms &amp; Conditions</a> and acknowledge our <a href="/privacy" className="underline">Privacy Policy</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
